<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <!-- Show message when no ID -->
          <v-card v-if="!hasValidId" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-information-outline</v-icon>
              <div class="text-h6 mb-2">No Attendance Summary Selected</div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                Please select an attendance summary from the list to view details.
              </div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="router.push({ name: 'attendance-summary' })"
              >
                <v-icon start>mdi-format-list-bulleted</v-icon>
                Go to Attendance Summary List
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Show loading state -->
          <v-card v-else-if="loading" elevation="1">
            <v-card-text class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
              <div class="text-body-1 mt-4">Loading attendance summary details...</div>
            </v-card-text>
          </v-card>

          <!-- Show error state -->
          <v-card v-else-if="error" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
              <div class="text-h6 mb-2 text-error">Error Loading Details</div>
              <div class="text-body-2 text-medium-emphasis mb-4">{{ error }}</div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="fetchAttendanceDetails"
                class="mr-2"
              >
                Retry
              </v-btn>
              <v-btn
                color="grey"
                variant="text"
                @click="router.push({ name: 'attendance-summary' })"
              >
                Back to List
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Show attendance summary details -->
          <v-card v-else-if="attendanceData" elevation="1">
            <v-card-title class="text-h5 pa-4">
              <v-icon class="me-2">mdi-calendar-check</v-icon>
              Attendance Summary Details - {{ attendanceData.code }}
              <v-progress-circular v-if="loading" indeterminate size="20" class="ml-2" color="primary"></v-progress-circular>
              <v-spacer></v-spacer>
              <v-chip
                :color="getStatusColor(attendanceData.status)"
                size="small"
                class="text-capitalize me-2"
              >
                {{ attendanceData.status === 1 ? 'Active' : 'Inactive' }}
              </v-chip>
              <v-chip
                :color="getApprovalStatusColor(attendanceData.approval_status)"
                size="small"
                class="text-capitalize"
              >
                {{ formatApprovalStatus(attendanceData.approval_status) }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pa-4">
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Code</div>
                  <div class="text-body-1 font-weight-medium">{{ attendanceData.code }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis"> Attendance Start Date</div>
                  <div class="text-body-1">{{ formatDate(attendanceData.start_date) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis"> Attendance End Date</div>
                  <div class="text-body-1">{{ formatDate(attendanceData.end_date) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Year / Month</div>
                  <div class="text-body-1">{{ attendanceData.year }} / {{ getMonthName(attendanceData.month) }}</div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row class="mb-3">
                <v-col cols="12" md="6">
                  <div class="text-h6">Employee Attendance Details</div>
                </v-col>
                <v-col cols="12" md="6" class="d-flex justify-end">
                  <v-btn-group
                    v-if="attendanceData?.details && attendanceData.details.length > 0"
                    variant="outlined"
                    density="compact"
                    size="x-small"
                    divided
                  >
                    <v-btn
                      prepend-icon="mdi mdi-download"
                      variant="flat"
                      color="success"
                      @click="exportToCSV"
                      :disabled="!attendanceData?.details?.length"
                    >
                      CSV
                    </v-btn>
                    <v-btn
                      prepend-icon="mdi mdi-download"
                      variant="flat"
                      color="primary"
                      @click="exportToExcel"
                      :disabled="!attendanceData?.details?.length"
                    >
                      EXCEL
                    </v-btn>
                    <v-btn
                      prepend-icon="mdi mdi-download"
                      variant="flat"
                      color="red"
                      @click="exportToPDF"
                      :disabled="!attendanceData?.details?.length"
                    >
                      PDF
                    </v-btn>
                  </v-btn-group>
                </v-col>
              </v-row>

              <v-data-table
                :headers="tableHeaders"
                :items="attendanceData.details || []"
                :loading="loading"
                class="mt-2"
                density="compact"
                hover
                :items-per-page="itemsPerPage"
                v-model:items-per-page="itemsPerPage"
              >
                <template #item.index="{ index }">
                  {{ index + 1 }}
                </template>
                <template #loading>
                  <div class="py-6 text-center text-medium-emphasis">
                    Loading employee attendance details...
                  </div>
                </template>
                <template #item.employee_code="{ item }">
                  <span class="font-weight-medium">{{ item.employee?.code || '—' }}</span>
                </template>
                <template #item.employee_name="{ item }">
                  {{ item.employee?.name || '—' }}
                </template>
                <template #item.total_days="{ item }">
                  <div class="text-end">{{ item.total_days || '0' }}</div>
                </template>
                <template #item.present_days="{ item }">
                  <div class="text-end">{{ item.present_days || '0' }}</div>
                </template>
                <template #item.absent_days="{ item }">
                  <div class="text-end">{{ item.absent_days || '0' }}</div>
                </template>
                <template #item.leave_days="{ item }">
                  <div class="text-end">{{ item.leave_days || '0' }}</div>
                </template>
                <template #item.late_count="{ item }">
                  <div class="text-end">{{ item.late_count || '0' }}</div>
                </template>
                <template #item.ot_hours="{ item }">
                  <div class="text-end">{{ item.ot_hours || '0.00' }}</div>
                </template>
                <template #item.total_weekends="{ item }">
                  <div class="text-end">{{ item.total_weekends || '0' }}</div>
                </template>
                <template #item.total_holidays="{ item }">
                  <div class="text-end">{{ item.total_holidays || '0' }}</div>
                </template>
                <template #item.payable_days="{ item }">
                  <div class="text-end"><span class="font-weight-medium">{{ item.payable_days || '0' }}</span></div>
                </template>
                <template #no-data>
                  <div class="py-6 text-center text-medium-emphasis">
                    No employee attendance details found.
                  </div>
                </template>
                <template #body.append v-if="attendanceData?.details && attendanceData.details.length > 0">
                  <tr class="v-data-table__tr" style="font-weight: bold; background-color: #f5f5f5;">
                    <td class="v-data-table__td text-center">—</td>
                    <td class="v-data-table__td" colspan="2">
                      <strong>Total:</strong>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.total_days }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.present_days }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.absent_days }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.leave_days }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.late_count }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.ot_hours.toFixed(2) }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.total_weekends }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.total_holidays }}</strong></div>
                    </td>
                    <td class="v-data-table__td text-end">
                      <div class="text-end"><strong>{{ totals.payable_days }}</strong></div>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>

            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                variant="outlined"
                @click="router.push({ name: 'attendance-summary' })"
              >
                <v-icon class="me-2">mdi-arrow-left</v-icon>
                Back to List
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import formatDateView from '@/plugins/dateFormatView'

const router = useRouter()
const route = useRoute()
const instance = getCurrentInstance()
const appContext = instance?.appContext
const store = useStore()
const loading = ref(false)
const error = ref(null)
const attendanceData = ref(null)
const itemsPerPage = ref(10)

// Options
const months = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
]

const tableHeaders = [
  { title: '#SL', key: 'index', sortable: false, width: '60px', align: 'center' },
  { title: 'Employee Code', key: 'employee_code', sortable: false },
  { title: 'Employee Name', key: 'employee_name', sortable: false },
  { title: 'Total Days', key: 'total_days', sortable: false, align: 'end' },
  { title: 'Present Days', key: 'present_days', sortable: false, align: 'end' },
  { title: 'Absent Days', key: 'absent_days', sortable: false, align: 'end' },
  { title: 'Leave Days', key: 'leave_days', sortable: false, align: 'end' },
  { title: 'Late Count', key: 'late_count', sortable: false, align: 'end' },
  { title: 'OT Hours', key: 'ot_hours', sortable: false, align: 'end' },
  { title: 'Weekends', key: 'total_weekends', sortable: false, align: 'end' },
  { title: 'Holidays', key: 'total_holidays', sortable: false, align: 'end' },
  { title: 'Payable Days', key: 'payable_days', sortable: false, align: 'end' }
]

// Computed
const hasValidId = computed(() => {
  return route.params.id && !isNaN(parseInt(route.params.id))
})

const totals = computed(() => {
  if (!attendanceData.value?.details || attendanceData.value.details.length === 0) {
    return {
      total_days: 0,
      present_days: 0,
      absent_days: 0,
      leave_days: 0,
      late_count: 0,
      ot_hours: 0,
      total_weekends: 0,
      total_holidays: 0,
      payable_days: 0
    }
  }

  return attendanceData.value.details.reduce((acc, item) => {
    acc.total_days += parseFloat(item.total_days || 0)
    acc.present_days += parseFloat(item.present_days || 0)
    acc.absent_days += parseFloat(item.absent_days || 0)
    acc.leave_days += parseFloat(item.leave_days || 0)
    acc.late_count += parseFloat(item.late_count || 0)
    acc.ot_hours += parseFloat(item.ot_hours || 0)
    acc.total_weekends += parseFloat(item.total_weekends || 0)
    acc.total_holidays += parseFloat(item.total_holidays || 0)
    acc.payable_days += parseFloat(item.payable_days || 0)
    return acc
  }, {
    total_days: 0,
    present_days: 0,
    absent_days: 0,
    leave_days: 0,
    late_count: 0,
    ot_hours: 0,
    total_weekends: 0,
    total_holidays: 0,
    payable_days: 0
  })
})

// Methods
const fetchAttendanceDetails = async () => {
  if (!hasValidId.value) {
    return
  }

  loading.value = true
  error.value = null

  try {
    const id = parseInt(route.params.id)
    const response = await store.dispatch('salaryReadyAttendance/getSalaryReadyAttendanceById', id)

    if (response.success) {
      attendanceData.value = response.data
    } else {
      error.value = response.message || 'Failed to load attendance summary details'
      toast.error(error.value)
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to load attendance summary details'
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  return formatDateView(dateString) || '—'
}

const getMonthName = (monthNumber) => {
  if (!monthNumber) return '—'
  const month = months.find(m => m.value === monthNumber)
  return month ? month.label : '—'
}

const getStatusColor = (status) => {
  return status === 1 ? 'success' : 'grey'
}

const formatApprovalStatus = (status) => {
  if (!status) return 'Pending'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const getApprovalStatusColor = (status) => {
  if (!status) return 'orange'
  switch (status.toLowerCase()) {
    case 'pending':
      return 'orange'
    case 'approved':
      return 'success'
    case 'rejected':
    case 'cancelled':
      return 'error'
    default:
      return 'grey'
  }
}

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

// Format number for export
const formatNumber = (value, fraction = 2) => {
  if (value === undefined || value === null || value === '' || isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

// Export functions
const exportToPDF = async () => {
  if (!attendanceData.value || !attendanceData.value.details || attendanceData.value.details.length === 0) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Attendance Summary Details</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: letter; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
          .totals-row { page-break-inside: avoid; }
          .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>ATTENDANCE SUMMARY DETAILS</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Code:</strong> ${attendanceData.value.code}</p>
              <p><strong>Start Date:</strong> ${formatDate(attendanceData.value.start_date)}</p>
              <p><strong>End Date:</strong> ${formatDate(attendanceData.value.end_date)}</p>
              <p><strong>Year / Month:</strong> ${attendanceData.value.year} / ${getMonthName(attendanceData.value.month)}</p>
              <p><strong>Status:</strong> ${attendanceData.value.status === 1 ? 'Active' : 'Inactive'}</p>
              <p><strong>Approval Status:</strong> ${formatApprovalStatus(attendanceData.value.approval_status)}</p>
            </div>
            <div class="col-sm-4 text-end">
              <p><strong>Total Records:</strong> ${attendanceData.value.details.length}</p>
            </div>
          </div>
          <table class="table mt-4">
            <thead>
              <tr>
                <th>SN</th>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th class="text-end">Total Days</th>
                <th class="text-end">Present Days</th>
                <th class="text-end">Absent Days</th>
                <th class="text-end">Leave Days</th>
                <th class="text-end">Late Count</th>
                <th class="text-end">OT Hours</th>
                <th class="text-end">Weekends</th>
                <th class="text-end">Holidays</th>
                <th class="text-end">Payable Days</th>
              </tr>
            </thead>
            <tbody>
              ${attendanceData.value.details.map((item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item.employee?.code || '—'}</td>
                  <td><strong>${item.employee?.name || '—'}</strong></td>
                  <td class="text-end">${item.total_days || '0'}</td>
                  <td class="text-end">${item.present_days || '0'}</td>
                  <td class="text-end">${item.absent_days || '0'}</td>
                  <td class="text-end">${item.leave_days || '0'}</td>
                  <td class="text-end">${item.late_count || '0'}</td>
                  <td class="text-end">${formatNumber(item.ot_hours, 2)}</td>
                  <td class="text-end">${item.total_weekends || '0'}</td>
                  <td class="text-end">${item.total_holidays || '0'}</td>
                  <td class="text-end">${item.payable_days || '0'}</td>
                </tr>
              `).join('')}
              <tr class="totals-row">
                <td colspan="3" class="text-end"><strong>Total:</strong></td>
                <td class="text-end"><strong>${totals.value.total_days}</strong></td>
                <td class="text-end"><strong>${totals.value.present_days}</strong></td>
                <td class="text-end"><strong>${totals.value.absent_days}</strong></td>
                <td class="text-end"><strong>${totals.value.leave_days}</strong></td>
                <td class="text-end"><strong>${totals.value.late_count}</strong></td>
                <td class="text-end"><strong>${formatNumber(totals.value.ot_hours, 2)}</strong></td>
                <td class="text-end"><strong>${totals.value.total_weekends}</strong></td>
                <td class="text-end"><strong>${totals.value.total_holidays}</strong></td>
                <td class="text-end"><strong>${totals.value.payable_days}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

const exportToExcel = () => {
  if (!attendanceData.value || !attendanceData.value.details || attendanceData.value.details.length === 0) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Attendance Summary Details'],
    [`Code: ${attendanceData.value.code}`],
    [`Start Date: ${formatDate(attendanceData.value.start_date)}`],
    [`End Date: ${formatDate(attendanceData.value.end_date)}`],
    [`Year / Month: ${attendanceData.value.year} / ${getMonthName(attendanceData.value.month)}`],
    [`Status: ${attendanceData.value.status === 1 ? 'Active' : 'Inactive'}`],
    [`Approval Status: ${formatApprovalStatus(attendanceData.value.approval_status)}`]
  ]
  
  data.push([], ['# SL', 'Employee Code', 'Employee Name', 'Total Days', 'Present Days', 'Absent Days', 'Leave Days', 'Late Count', 'OT Hours', 'Weekends', 'Holidays', 'Payable Days'], [])

  // Add data rows
  attendanceData.value.details.forEach((item, index) => {
    data.push([
      index + 1,
      item.employee?.code || '—',
      item.employee?.name || '—',
      item.total_days || '0',
      item.present_days || '0',
      item.absent_days || '0',
      item.leave_days || '0',
      item.late_count || '0',
      formatNumber(item.ot_hours, 2),
      item.total_weekends || '0',
      item.total_holidays || '0',
      item.payable_days || '0'
    ])
  })

  // Add totals row
  if (attendanceData.value.details.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      totals.value.total_days,
      totals.value.present_days,
      totals.value.absent_days,
      totals.value.leave_days,
      totals.value.late_count,
      formatNumber(totals.value.ot_hours, 2),
      totals.value.total_weekends,
      totals.value.total_holidays,
      totals.value.payable_days
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'attendance_summary_details_' + attendanceData.value.code + '_' + proxy.$fileDate())
  }
}

const exportToCSV = () => {
  if (!attendanceData.value || !attendanceData.value.details || attendanceData.value.details.length === 0) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Attendance Summary Details'],
    [`Code: ${attendanceData.value.code}`],
    [`Start Date: ${formatDate(attendanceData.value.start_date)}`],
    [`End Date: ${formatDate(attendanceData.value.end_date)}`],
    [`Year / Month: ${attendanceData.value.year} / ${getMonthName(attendanceData.value.month)}`],
    [`Status: ${attendanceData.value.status === 1 ? 'Active' : 'Inactive'}`],
    [`Approval Status: ${formatApprovalStatus(attendanceData.value.approval_status)}`]
  ]
  
  data.push([], ['# SL', 'Employee Code', 'Employee Name', 'Total Days', 'Present Days', 'Absent Days', 'Leave Days', 'Late Count', 'OT Hours', 'Weekends', 'Holidays', 'Payable Days'], [])

  // Add data rows
  attendanceData.value.details.forEach((item, index) => {
    data.push([
      index + 1,
      item.employee?.code || '—',
      item.employee?.name || '—',
      item.total_days || '0',
      item.present_days || '0',
      item.absent_days || '0',
      item.leave_days || '0',
      item.late_count || '0',
      formatNumber(item.ot_hours, 2),
      item.total_weekends || '0',
      item.total_holidays || '0',
      item.payable_days || '0'
    ])
  })

  // Add totals row
  if (attendanceData.value.details.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      totals.value.total_days,
      totals.value.present_days,
      totals.value.absent_days,
      totals.value.leave_days,
      totals.value.late_count,
      formatNumber(totals.value.ot_hours, 2),
      totals.value.total_weekends,
      totals.value.total_holidays,
      totals.value.payable_days
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
    proxy.$exportTableToCSV(data, 'attendance_summary_details_' + attendanceData.value.code + '_' + proxy.$fileDate())
  }
}

// Watch for route parameter changes
watch(() => route.params.id, (newId) => {
  if (newId) {
    fetchAttendanceDetails()
  }
}, { immediate: true })

// Lifecycle
onMounted(() => {
  if (hasValidId.value) {
    fetchAttendanceDetails()
  }
})
</script>

<style scoped>
</style>

